import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, User, Receipt, FileText } from 'lucide-react';
import Layout from '@/components/Layout';

export default function TenantDetailPage() {
  const { id } = useParams();
  const [tenant, setTenant] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTenantDetails();
  }, [id]);

  const fetchTenantDetails = async () => {
    try {
      const [tenantRes, ledgerRes] = await Promise.all([
        api.getTenantDetail(id),
        api.getTenantLedger(id)
      ]);
      setTenant(tenantRes.data);
      setLedger(ledgerRes.data);
    } catch (error) {
      toast.error('Failed to fetch tenant details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  if (!tenant) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-600">Tenant not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <Button
          onClick={() => navigate('/tenants')}
          variant="ghost"
          data-testid="back-to-tenants"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Tenants
        </Button>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">{tenant.name.charAt(0)}</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 font-heading">{tenant.name}</h1>
                <p className="text-slate-600">{tenant.phone}</p>
                {tenant.email && <p className="text-sm text-slate-500">{tenant.email}</p>}
              </div>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
              tenant.status === 'active'
                ? 'bg-green-100 text-green-700'
                : 'bg-slate-200 text-slate-700'
            }`}>
              {tenant.status === 'active' ? 'Active' : 'Vacated'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700 font-medium mb-1">Monthly Rent</p>
              <p className="text-2xl font-bold text-blue-900">₹{tenant.monthlyRent.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-700 font-medium mb-1">Deposit Amount</p>
              <p className="text-2xl font-bold text-purple-900">₹{(tenant.depositAmount || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className={`p-4 rounded-lg border ${
              (tenant.balance || 0) >= 0
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <p className={`text-sm font-medium mb-1 ${
                (tenant.balance || 0) >= 0 ? 'text-green-700' : 'text-red-700'
              }`}>
                {(tenant.balance || 0) >= 0 ? 'Advance Balance' : 'Pending Dues'}
              </p>
              <p className={`text-2xl font-bold ${
                (tenant.balance || 0) >= 0 ? 'text-green-900' : 'text-red-900'
              }`}>
                ₹{Math.abs(tenant.balance || 0).toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200">
            <div>
              <p className="text-sm text-slate-600 mb-1">Join Date</p>
              <p className="font-semibold text-slate-900">
                {new Date(tenant.joinDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">ID Proof</p>
              <p className="font-semibold text-slate-900">{tenant.idProofType}</p>
              <p className="text-xs text-slate-500">{tenant.idProofNumber}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600 mb-1">Emergency Contact</p>
              <p className="font-semibold text-slate-900">{tenant.emergencyContact}</p>
            </div>
            {tenant.vacateDate && (
              <div>
                <p className="text-sm text-slate-600 mb-1">Vacate Date</p>
                <p className="font-semibold text-slate-900">
                  {new Date(tenant.vacateDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 font-heading">Ledger History</h2>
          </div>

          {ledger.length === 0 ? (
            <p className="text-slate-600 text-center py-8">No transactions yet</p>
          ) : (
            <div className="space-y-2">
              {ledger.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid={`ledger-entry-${entry.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 text-xs font-semibold rounded ${
                        entry.entryType === 'RENT_PAYMENT' || entry.entryType === 'DEPOSIT_PAYMENT'
                          ? 'bg-green-100 text-green-700'
                          : entry.entryType === 'RENT_CHARGE'
                          ? 'bg-orange-100 text-orange-700'
                          : entry.entryType === 'DEPOSIT_REFUND'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-slate-200 text-slate-700'
                      }`}>
                        {entry.entryType.replace('_', ' ')}
                      </span>
                      <span className="text-sm text-slate-500">
                        {new Date(entry.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">{entry.description}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      entry.amount >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {entry.amount >= 0 ? '+' : ''}₹{Math.abs(entry.amount).toLocaleString('en-IN')}
                    </p>
                    {entry.runningBalance != null && (
                      <p className="text-sm text-slate-500">
                        Balance: ₹{entry.runningBalance.toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
